import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Wizard, Shelf } from 'components/widgets';
import AppSelection from '../containers/AppSelection';
import MetricsSelection from '../containers/MetricsSelection';
import RoleSelection from '../containers/RoleSelection';
import ScreenSelection from '../containers/ScreenSelection';
import SourceSelection from '../containers/SourceSelection';
import SummaryView from '../containers/SummaryView';
import UserRegistrationForm from '../containers/UserRegistrationForm';
import GenericDimensionSelection from '../containers/GenericDimensionSelection';
import { SHELF_STEPS, WIZARD_MODES, RESTRICTED_ROLES, ROLE_STANDARD } from '../utils';

function TeamManagementShelf({
    mode = '',
    role = ROLE_STANDARD,
    permissionDimensions = {},
    shelfSteps = [],
    editedMember = {},
    shelf = { open: false, shelfSize: 'medium' },
    onCloseShelf = () => {},
    onManagePermissionsFinished = () => {},
    onWizardActionTypeChanged = () => {},
}) {
    const [activeStep, setActiveStep] = useState('');

    useDeepCompareEffect(() => {
        const wizardStep =
            mode === WIZARD_MODES.edit && RESTRICTED_ROLES.includes(editedMember.role.type)
                ? shelfSteps[shelfSteps.length - 1]
                : shelfSteps[0];

        setActiveStep(wizardStep);
    }, [shelfSteps, mode, editedMember]);

    const _onWizardPageChanged = (_, currentStep, actionType = '') => {
        setActiveStep(currentStep.id);
        onWizardActionTypeChanged(actionType);
    };

    const WIZARD_STEPS_DATA = {
        [SHELF_STEPS.roleSelection]: {
            element: RoleSelection,
            title: 'Role Selection',
        },
        [SHELF_STEPS.userDetails]: {
            element: UserRegistrationForm,
            title: 'User Details',
        },
        [SHELF_STEPS.appSelection]: {
            element: AppSelection,
            title: 'App Selection',
        },
        [SHELF_STEPS.sourceSelection]: {
            element: SourceSelection,
            title: 'Source Selection',
        },
        [SHELF_STEPS.metricsSelection]: {
            element: MetricsSelection,
            title: 'Metrics Selection',
        },
        [SHELF_STEPS.screenSelection]: {
            element: ScreenSelection,
            title: 'Screen Selection',
        },
        [SHELF_STEPS.review]: {
            element: SummaryView,
            title: 'Review',
            elementProps: {
                onEditClicked: id => _onWizardPageChanged(id, id, WIZARD_MODES.edit),
            },
        },
    };

    const wizardStepsElements = useMemo(() => {
        return shelfSteps.map(id => {
            const step = WIZARD_STEPS_DATA[id];

            if (step) {
                const { element: StepElement, title, elementProps } = step;
                return <StepElement id={id} key={id} label={title} {...elementProps} />;
            }

            const dimension = Object.values(permissionDimensions).find(d => d.name === id);
            const { name, displayName, values } = dimension;

            return (
                <GenericDimensionSelection
                    id={name}
                    key={name}
                    dimension={name}
                    displayName={displayName}
                    values={values}
                    label={`${displayName} Selection`}
                />
            );
        });
    }, [shelfSteps]);

    const showStepper = RESTRICTED_ROLES.includes(role) && activeStep !== SHELF_STEPS.roleSelection;
    const shelfSize = activeStep === SHELF_STEPS.roleSelection ? 'medium' : 'large';

    return (
        <Shelf {...shelf} shelfSize={shelfSize} onClose={onCloseShelf}>
            <Wizard
                onWizardFinish={onManagePermissionsFinished}
                onWizardCancel={onCloseShelf}
                onWizardPageChanged={_onWizardPageChanged}
                showStepper={showStepper}
                activeStep={activeStep}
            >
                {wizardStepsElements}
            </Wizard>
        </Shelf>
    );
}

TeamManagementShelf.propTypes = {
    shelfSteps: PropTypes.arrayOf(PropTypes.string),
    mode: PropTypes.string,
    role: PropTypes.number,
    shelf: PropTypes.shape({
        open: PropTypes.bool,
        shelfSize: PropTypes.string,
    }),
    editedMember: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        role: PropTypes.shape({
            type: PropTypes.string,
        }),
    }),
    permissionDimensions: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            values: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    displayName: PropTypes.string.isRequired,
                })
            ).isRequired,
            selected: PropTypes.shape({
                values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
                addFuture: PropTypes.bool,
            }).isRequired,
        })
    ),
    onManagePermissionsFinished: PropTypes.func,
    onCloseShelf: PropTypes.func,
    onWizardActionTypeChanged: PropTypes.func,
};

export default withLocalize(TeamManagementShelf);
